import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import { Loader2 } from "lucide-react";

const ROLE_LABELS = {
  buyer: "Buyer Terms & Conditions",
  seller: "Seller Terms & Conditions",
  cam_vendor: "CAM Vendor Terms & Conditions",
  privacy: "Privacy Policy",
};

/** Fetches a single T&C doc and renders it as scrollable markdown.
 *  Height-constrained by default so it stays inside signup / accept modals.
 *  Pass `expanded` to render at full page height (used by /terms/* routes). */
export default function TermsViewer({ role, expanded = false, className = "", onLoaded }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get(`/terms/${role}/current`)
      .then((r) => {
        if (!alive) return;
        setDoc(r.data);
        onLoaded && onLoaded(r.data);
      })
      .catch((e) => alive && setErr(e?.response?.data?.detail || "Failed to load"));
    return () => { alive = false; };
  }, [role]);

  if (err) return <div className="text-sm text-red-600" data-testid={`terms-error-${role}`}>{err}</div>;
  if (!doc) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-6" data-testid={`terms-loading-${role}`}>
        <Loader2 className="h-4 w-4 animate-spin" /> Loading terms…
      </div>
    );
  }

  const containerCls = expanded
    ? `prose prose-slate max-w-none ${className}`
    : `prose prose-sm max-w-none border border-border rounded-md p-4 bg-card overflow-y-auto ${className || "h-64"}`;

  return (
    <div className={containerCls} data-testid={`terms-viewer-${role}`}>
      <p className="text-xs font-mono text-muted-foreground m-0 mb-2">
        {ROLE_LABELS[role]} • Version {doc.version} • Effective {doc.effective_at?.slice(0, 10)}
      </p>
      <ReactMarkdown>{doc.body_md}</ReactMarkdown>
    </div>
  );
}

export { ROLE_LABELS };
